import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as i1 from '@eucoms/common-components';
import { AppConfigService } from '@eucoms/common-components';
import * as i2 from 'ng-recaptcha';
import { RecaptchaV3Module, RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha';
import { of } from 'rxjs';
import { first, map, shareReplay, switchMap } from 'rxjs/operators';

class RecaptchaTokenService {
    constructor(appConfigService, appSessionService, apiBroker, reCaptchaV3Service) {
        var _a;
        this.appConfigService = appConfigService;
        this.appSessionService = appSessionService;
        this.apiBroker = apiBroker;
        this.reCaptchaV3Service = reCaptchaV3Service;
        const waitForAppConfig$ = (((_a = this.appConfigService.config) === null || _a === void 0 ? void 0 : _a.opCo) != null)
            ? of(this.appConfigService.config)
            : this.appConfigService.config$.pipe(first(config => config.opCo != null));
        this.recaptchaSiteV3Key$ = waitForAppConfig$.pipe(map(config => config.reCaptchaV3SiteKey), shareReplay(1));
        this.recaptchaSiteV3Key$.subscribe(key => {
            this.recaptchaSiteV3Key = key;
        });
    }
    /**
     * Registers the recaptcha interceptor with the ApiBrokerService
     * Should be called before any API calls are made
     */
    registerRecaptchaInterceptor() {
        this.apiBroker.registerInterceptor({
            id: 'recaptchaInterceptor',
            handleRequest: (request, next) => this.processRequest(request, next),
            allowMultiple: false
        });
    }
    processRequest(apiRequest, next) {
        const shouldUseRecaptcha = this.shouldUseRecaptcha(apiRequest);
        if (!shouldUseRecaptcha)
            return next(apiRequest);
        return this.reCaptchaV3Service.execute('importantAction').pipe(map((token) => {
            apiRequest.options = Object.assign({}, apiRequest.options);
            apiRequest.options.headers = Object.assign({}, apiRequest.options.headers, { 'recaptcha-token': token });
            return apiRequest;
        }), switchMap(response => next(response)));
    }
    shouldUseRecaptcha(apiRequest) {
        if (!this.recaptchaSiteV3Key)
            return false;
        if (apiRequest.url.includes('/auth/'))
            return false;
        return true;
    }
}
RecaptchaTokenService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RecaptchaTokenService, deps: [{ token: i1.AppConfigService }, { token: i1.AppSessionService }, { token: i1.ApiBrokerService }, { token: i2.ReCaptchaV3Service }], target: i0.ɵɵFactoryTarget.Injectable });
RecaptchaTokenService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RecaptchaTokenService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RecaptchaTokenService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.AppConfigService }, { type: i1.AppSessionService }, { type: i1.ApiBrokerService }, { type: i2.ReCaptchaV3Service }]; } });

function getReCaptchaV3SiteKey(config) {
    return config.config.reCaptchaV3SiteKey;
}
/**
 * Usage instructions
 * Import this module in your application,
 * Inject and call the `registerRecaptchaInterceptor` method of the `RecaptchaTokenService` service before any API calls are made
 * through `ApiBrokerService` requests.
 * For example, in your AppComponent constructor.
 */
class RecaptchaModule {
}
RecaptchaModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RecaptchaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RecaptchaModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: RecaptchaModule, imports: [CommonModule, RecaptchaV3Module] });
RecaptchaModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RecaptchaModule, providers: [
        RecaptchaTokenService,
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useFactory: getReCaptchaV3SiteKey,
            deps: [AppConfigService],
        },
    ], imports: [CommonModule, RecaptchaV3Module] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: RecaptchaModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, RecaptchaV3Module],
                    providers: [
                        RecaptchaTokenService,
                        {
                            provide: RECAPTCHA_V3_SITE_KEY,
                            useFactory: getReCaptchaV3SiteKey,
                            deps: [AppConfigService],
                        },
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { RecaptchaModule, RecaptchaTokenService, getReCaptchaV3SiteKey };
